import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { notificationService, userService, campusService } from '../../api';
import {
  Send, Bell, Info, CheckCircle, AlertTriangle, XCircle,
  BookOpen, Award, FileText, Megaphone, Users, Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';
import AppSelect from '../../components/ui/AppSelect'

// ── Category config ───────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'info',         label: 'ℹ️  Info',         color: '#2FA7A1' },
  { value: 'success',      label: '✅  Success',       color: '#10b981' },
  { value: 'warning',      label: '⚠️  Warning',       color: '#f59e0b' },
  { value: 'error',        label: '🚨  Error',          color: '#ef4444' },
  { value: 'enrollment',   label: '📚  Enrollment',    color: '#2F7C7B' },
  { value: 'assessment',   label: '📝  Assessment',    color: '#F2B542' },
  { value: 'certificate',  label: '🏅  Certificate',   color: '#f59e0b' },
  { value: 'announcement', label: '📢  Announcement',  color: '#2FA7A1' },
];

const CATEGORY_COLOR = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.color]));

const NOTIF_TYPES = [
  { value: 'in_app', label: '📱 In-App' },
  { value: 'email',  label: '📧 Email' },
];

function CategoryBadge({ category }) {
  const color = CATEGORY_COLOR[category] || '#2F7C7B';
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
      background: `${color}18`, color,
    }}>
      {category}
    </span>
  );
}

function StatusBadge({ status, isRead }) {
  if (isRead !== undefined) {
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
        background: isRead ? '#10b98118' : '#2F7C7B18',
        color: isRead ? '#10b981' : '#2F7C7B',
      }}>
        {isRead ? 'Read' : 'Unread'}
      </span>
    );
  }
  const colors = { sent: '#10b981', pending: '#f59e0b', failed: '#ef4444' };
  const color = colors[status] || '#6b7280';
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
      background: `${color}18`, color,
    }}>
      {status}
    </span>
  );
}

// ── Input styles helper ───────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px',
  borderRadius: 10, border: '1px solid var(--color-border)',
  fontSize: 13, background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [creating, setCreating] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  const [form, setForm] = useState({
    notification_type: 'in_app',
    category: 'info',
    subject: '',
    message: '',
    target: 'all',   // 'all' | 'campus' | 'user'
    campus: '',
    user: '',
  });

  // Load campuses for broadcast target selector
  useEffect(() => {
    campusService.getCampuses().then((r) => setCampuses(r.data.results || r.data || [])).catch(() => {});
  }, []);

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (filterCategory) params.category = filterCategory;
    if (filterStatus) params.status = filterStatus;
    if (search) params.search = search;
    notificationService.getNotifications(params)
      .then((res) => {
        const { results, count } = parsePaginated(res);
        setNotifications(results);
        setTotalCount(count);
      })
      .catch(() => toast.error('Failed to load notifications'))
      .finally(() => setLoading(false));
  }, [page, pageSize, filterCategory, filterStatus, search]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error('Message is required'); return; }
    setCreating(true);
    try {
      const payload = {
        notification_type: form.notification_type,
        category: form.category,
        subject: form.subject,
        message: form.message,
      };
      if (form.target === 'campus' && form.campus) payload.campus = parseInt(form.campus);
      if (form.target === 'user' && form.user) payload.user = parseInt(form.user);

      const res = await notificationService.createNotification(payload);
      const created = res.data.created;
      toast.success(created != null ? `Sent to ${created} user${created !== 1 ? 's' : ''}!` : 'Notification sent!');
      setForm({ notification_type: 'in_app', category: 'info', subject: '', message: '', target: 'all', campus: '', user: '' });
      fetchNotifications();
    } catch {
      toast.error('Failed to send notification');
    } finally {
      setCreating(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: 'var(--color-text-primary)' }}>
            Notifications
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: 13 }}>
            Send and manage platform-wide notifications across your campus.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 24, alignItems: 'start' }}>

          {/* ── Compose Panel ── */}
          <div style={{
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 18,
            padding: 24,
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Send size={16} />
              Compose Notification
            </h2>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Delivery type */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  DELIVERY TYPE
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {NOTIF_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, notification_type: t.value }))}
                      style={{
                        flex: 1, padding: '8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        border: '1px solid var(--color-border)', cursor: 'pointer',
                        background: form.notification_type === t.value ? '#2F7C7B' : 'transparent',
                        color: form.notification_type === t.value ? '#fff' : 'var(--color-text-secondary)',
                        transition: 'all 0.2s',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  CATEGORY
                </label>
                <AppSelect name="category" value={form.category} onChange={handleChange} style={inputStyle} id="notif-category-select">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </AppSelect>
              </div>

              {/* Target audience */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  SEND TO
                </label>
                <AppSelect name="target" value={form.target} onChange={handleChange} style={inputStyle} id="notif-target-select">
                  <option value="all">All my students</option>
                  <option value="campus">Specific campus</option>
                  <option value="user">Specific user (ID)</option>
                </AppSelect>
                {form.target === 'campus' && (
                  <AppSelect name="campus" value={form.campus} onChange={handleChange} style={{ ...inputStyle, marginTop: 8 }} id="notif-campus-select">
                    <option value="">Select campus…</option>
                    {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </AppSelect>
                )}
                {form.target === 'user' && (
                  <input
                    type="number"
                    name="user"
                    value={form.user}
                    onChange={handleChange}
                    placeholder="User ID"
                    style={{ ...inputStyle, marginTop: 8 }}
                    id="notif-user-id-input"
                  />
                )}
              </div>

              {/* Subject */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  SUBJECT (optional)
                </label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="e.g. New course available"
                  style={inputStyle}
                  id="notif-subject-input"
                />
              </div>

              {/* Message */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  MESSAGE *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Write your notification message…"
                  required
                  style={{ ...inputStyle, resize: 'vertical' }}
                  id="notif-message-textarea"
                />
              </div>

              <button
                type="submit"
                disabled={creating}
                id="notif-send-btn"
                style={{
                  padding: '12px', borderRadius: 12, fontWeight: 700, fontSize: 14,
                  background: creating ? 'var(--color-border)' : 'linear-gradient(135deg, #2F7C7B, #F2B542)',
                  color: '#fff', border: 'none', cursor: creating ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.2s',
                }}
              >
                <Send size={15} />
                {creating ? 'Sending…' : 'Send Notification'}
              </button>
            </form>
          </div>

          {/* ── Notifications List ── */}
          <div>
            {/* Filters bar */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search notifications…"
                  style={{ ...inputStyle, paddingLeft: 34 }}
                  id="notif-search-input"
                />
              </div>
              <AppSelect
                value={filterCategory}
                onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
                style={{ ...inputStyle, width: 'auto' }}
                id="notif-filter-category"
              >
                <option value="">All categories</option>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </AppSelect>
              <AppSelect
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                style={{ ...inputStyle, width: 'auto' }}
                id="notif-filter-status"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </AppSelect>
            </div>

            {/* List */}
            {loading ? (
              <ListLoadingState label="Loading notifications…" />
            ) : notifications.length === 0 ? (
              <ListEmptyState icon={Bell} title="No notifications yet" description="Sent notifications will appear here." />
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      style={{
                        display: 'flex', gap: 14, padding: '16px 18px',
                        background: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 14,
                      }}
                    >
                      <div style={{
                        flexShrink: 0, width: 36, height: 36, borderRadius: 9,
                        background: `${CATEGORY_COLOR[notif.category] || '#2F7C7B'}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Bell size={15} style={{ color: CATEGORY_COLOR[notif.category] || '#2F7C7B' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          {notif.subject && (
                            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-primary)' }}>
                              {notif.subject}
                            </span>
                          )}
                          <CategoryBadge category={notif.category} />
                          <StatusBadge status={notif.status} isRead={notif.is_read} />
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                          {notif.message}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          To: <strong>{notif.user_name || `User #${notif.user}`}</strong>
                          {' · '}
                          {notif.time_ago || new Date(notif.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <ListPagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminNotifications;
